import React, { useEffect, useState } from 'react';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { useRecordContext } from 'react-admin';
import { API_BASE_URL, authFetch } from '../../../apiClient.ts';

interface GroupTreeNode {
  id: number;
  name: string;
  parentId?: number | null;
  children?: GroupTreeNode[];
}

const getAllNodeIds = (node: GroupTreeNode): string[] => {
  const ids = [String(node.id)];

  node.children?.forEach((child) => {
    ids.push(...getAllNodeIds(child));
  });

  return ids;
};

const renderTree = (node: GroupTreeNode) => (
  <TreeItem
    key={node.id}
    itemId={String(node.id)}
    label={
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <span>{node.name}</span>
        <IconButton
          size="small"
          edge="end"
          sx={{ p: 0.5 }}
          component={RouterLink}
          to={`/groups/${node.id}/show`}
          onClick={(event) => event.stopPropagation()}
        >
          <ArrowForwardIcon fontSize="small" />
        </IconButton>
      </Box>
    }
  >
    {node.children?.map((child) => renderTree(child))}
  </TreeItem>
);

const ReadOnlyGroupSubgroups: React.FC = () => {
  const group = useRecordContext<{ id?: number }>();
  const [groupTree, setGroupTree] = useState<GroupTreeNode | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!group?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    authFetch(`${API_BASE_URL}/groups/tree/${group.id}`)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Failed to load subgroup tree (${response.status})`);
        }

        return response.json();
      })
      .then((data: GroupTreeNode) => {
        setGroupTree(data);
        setExpanded(getAllNodeIds(data));
      })
      .catch((error) => {
        console.error('Failed to load group tree', error);
        setGroupTree(null);
      })
      .finally(() => setLoading(false));
  }, [group?.id]);

  if (!group?.id) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Loading subgroup tree...
        </Typography>
      </Box>
    );
  }

  if (!groupTree) {
    return (
      <Typography variant="body2" color="text.secondary">
        No subgroup tree available.
      </Typography>
    );
  }

  return (
    <SimpleTreeView
      selectedItems={String(group.id)}
      expandedItems={expanded}
      onExpandedItemsChange={(_, itemIds) => setExpanded(itemIds)}
    >
      {renderTree(groupTree)}
    </SimpleTreeView>
  );
};

export default ReadOnlyGroupSubgroups;
